import { useEffect, useRef, useState } from 'react'
import { History, ChevronDown } from 'lucide-react'

// Short + stable relative-time formatter. Avoids pulling in a date
// library for the three buckets the dropdown actually shows.
function relativeTime(iso: string): string {
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 86400 * 2) return 'yesterday'
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`
  return new Date(iso).toLocaleDateString()
}

export interface VersionEntry {
  id: string
  versionNum: number
  createdAt: string
}

export function VersionPicker<T extends VersionEntry>({
  versions, value, onChange, ariaLabel = 'Versions', buttonClassName,
}: {
  versions: T[]
  value: number
  onChange: (v: number) => void
  ariaLabel?: string
  // Override the default trigger button classes. Used by callers that
  // need the picker to match neighbouring buttons (e.g. the side
  // panel's XLSX/Save pair).
  buttonClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const latest = versions[versions.length - 1].versionNum

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Newest first — matches how version histories read elsewhere.
  const rows = [...versions].reverse()

  return (
    <div ref={rootRef} className="relative inline-block align-middle ml-2">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${ariaLabel} v${value} of v${latest}. ${versions.length} versions available.`}
        className={buttonClassName ?? "inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"}
      >
        <History size={11} />
        <span>v{value}</span>
        {value === latest && <span>Latest</span>}
        <ChevronDown
          size={11}
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label={ariaLabel}
          // z-[200] beats Handsontable's sticky column headers
          // (internal z-index ~101), so the dropdown renders above
          // the grid when rendered just above it.
          className="absolute z-[200] right-0 mt-1 min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-left"
        >
          {rows.map((v) => {
            const selected = v.versionNum === value
            const isLatest = v.versionNum === latest
            return (
              <li key={v.id}>
                <button
                  role="option"
                  aria-selected={selected}
                  type="button"
                  onClick={() => {
                    onChange(v.versionNum)
                    setOpen(false)
                    buttonRef.current?.focus()
                  }}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-1.5 text-xs text-left hover:bg-gray-50 ${
                    selected ? 'bg-slate-100 text-slate-800' : 'text-gray-700'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="font-medium">v{v.versionNum}</span>
                    {isLatest && (
                      <span className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold">
                        latest
                      </span>
                    )}
                  </span>
                  <span className="text-[11px] text-gray-400 font-normal">
                    {relativeTime(v.createdAt)}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default VersionPicker
